<template>
  <div>
    <template v-if="achievements.length > 0">
      <template v-if="!isDetail">
        <ListHeader
          v-if="showAll"
          tableId="listTable"
          :filteredList="filteredAchievements"
          :fullList="achievements"
          :search="search"
          :fields="achievementFields"
          :pagenate="pagenate"
          :textPlaceholder="searchPlaceHolder"
          @updateSearch="updateSearch"
          @changePage="changePage"
        >
          <div>
            <app-button class="default" @click="openEditForm(0)" style="margin-right: 10px">
              {{ addButtonText }}
            </app-button>
            <b-button variant="info" v-b-modal.downloadConfirm>CSVダウンロード</b-button>
          </div>
        </ListHeader>
        <app-button v-else class="default" @click="openEditForm(0)" style="margin-bottom: 10px">
          {{ addButtonText }}
        </app-button>

        <ConfirmModal
          id="downloadConfirm"
          @on-click-confirm="downloadList()"
          @on-click-cancel="closeModal('downloadConfirm')"
        >
          <template v-for="(message, index) in getDownloadConfirmMessage()">
            {{ message }}
            <br :key="'download-message-' + index" />
          </template>
        </ConfirmModal>
        <ConfirmModal
          id="deleteConfirm"
          :text="getDeleteConfirmMessage()"
          @on-click-confirm="deleteAchievement(deleteId)"
          @on-click-cancel="closeModal('deleteConfirm')"
        ></ConfirmModal>

        <template v-if="filteredAchievements.length > 0">
          <!-- テーブル -->
          <b-table
            id="listTable"
            small
            stacked="sm"
            striped
            :items="filteredAchievements"
            :fields="tableFields"
            :per-page="pagenate.perPage"
            :sordBy="pagenate.sortBy"
            :sortDesc="pagenate.sortDesc"
            :current-page="pagenate.currentPage"
            responsive
            @sort-changed="changeSort"
            :no-local-sorting="true"
            class="announcement-table"
          >
            <template v-slot:cell(title)="data">
              <b-link @click.prevent.stop="showDetail(data.item.achievement_id, data.index)">
                {{ data.item.title }}
              </b-link>
              <span class="draft" v-if="data.item.is_draft === '1'">(下書き)</span>
            </template>
            <template v-slot:cell(is_draft)="data">
              {{ stateText(data.item.is_draft) }}
            </template>
            <template v-slot:cell(issued_at)="data">
              {{ getArrangedIssuedDate(data.item.issued_at) }}
              <template v-if="getArrangedUpdatedAt(data.item.updated_at)"></template>
              <br />
              <span class="text-muted small">
                最終更新:{{ getArrangedUpdatedAt(data.item.updated_at, data.item.issued_at) }}
              </span>
            </template>
            <template v-slot:cell(achievement_type_id)="data">
              {{ getAchievementType(data.item.achievement_type_id, achievementTypes) }}
            </template>
            <template v-slot:cell(management)="data">
              <b-container align-h="start" class="icons">
                <b-row align-v="center">
                  <!--編集・削除-->
                  <b-col class="px-3" :cols="colSize" v-if="data.item.isEditable" @click="openEditForm(data.item.achievement_id)">
                      <SHIcons class="edit-achievement" name="draft" iconColor="#68c3d4"/>
                  </b-col>
                  <b-col class="px-3" :cols="colSize" v-if="data.item.isEditable" @click="deleteAchievement(data.item.achievement_id, data.item.title)">
                    <SHIcons class="delete-achievement" name="trash" iconColor="#68c3d4" />
                  </b-col>
                  <b-col class="px-3" v-if="!data.item.isEditable" :cols="colSize * 2">
                    <span v-if="isAuth" class="text-muted small">---</span>
                  </b-col>

                  <!-- typetalk関連 -->
                  <b-col class="px-3" cols="4" v-if="!isAuth">
                    <b-icon icon="globe" v-if="data.item.typetalk_post_id" class="locate-typetalk" v-b-popover.hover="'投稿したtypetalkの投稿を開きます。'" @click.prevent.stop="showTypetalk(data.item.typetalk_post_id)"></b-icon>
                    <b-img :src="typetalkIcon" v-if="!data.item.typetalk_post_id && data.item.is_draft !== '1'" class="share-achievement" alt="typetalk連携" @click="handleClickTypetalk(data.item.title, data.item.achievement_id)" v-b-popover.hover="'typetalkに成果情報を投稿します。'" />
                  </b-col>
                </b-row>
              </b-container>
            </template>
          </b-table>
        </template>

        <!-- 検索結果に合致する要素がない場合 -->
        <template v-else>
          <div class="bg-info text-white">表示できるデータがありません。</div>
        </template>
        <slot><!--もっと見る はここに入る--></slot>
      </template>

      <!-- 詳細画面 -->
      <template v-else>
        <template>
          <Pagenation
            :index="detailIndex"
            :total="filteredAchievements.length"
            @showOther="showOther"
            @showList="showList"
          >
            <b-button
              :disabled="! selectedAchievement.isEditable"
              @click="openEditForm( selectedAchievement.achievement_id)"
              variant="outline-info rounded-pill"
            >
              <b-icon icon="pencil" variant=""></b-icon>
              &nbsp;編集
            </b-button>
            &nbsp;&nbsp;&nbsp;&nbsp;
            <b-button
              :disabled="! selectedAchievement.isEditable"
              @click="deleteAchievementFromDetail()"
              variant="outline-secondary rounded-pill"
            >
              <b-icon icon="trash" variant=""></b-icon>
              &nbsp;削除
            </b-button>
          </Pagenation>
        </template>
        <AchievementDetail :achievement="selectedAchievement" :isAdmin="isAuth" />
      </template>
    </template>

    <!-- 成果がない場合 -->
    <template v-else>
      <app-button class="default" style="margin-bottom: 10px" @click="openEditForm(0)">{{ addButtonText }}</app-button>
      <div class="bg-info text-white">成果は登録されていません。</div>
    </template>

    <b-modal id="achievement-modal" :no-close-on-backdrop="true" :title="modalTitle" :hide-footer="true" size="xl">
      <AchievementEdit
        :data="achievementInfo"
        :achievementOptions="achievementOptions"
        @update="update"
        @closeModal="closeEditModal($event)"
      ></AchievementEdit>
    </b-modal>
    <Typetalk :achievementName="achievementTitle" :achievementId="achievementId" @click-send="handleClickShare"></Typetalk>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import listJs from '@/transformer/list'
import baseJs from '@/transformer/base'
import { downloadCsv } from '@/transformer/file'
import Pagenation from '@/components/Pagenation.vue'
import ListHeader from '@/components/ListHeader.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import AchievementEdit from '@/components/AchievementEdit.vue'
import Typetalk from '@/components/Typetalk.vue'

import AchievementDetail from '@/components/Achievement/AchievementDetail'

import AppButton from '@/components/presentationals/AppButton'
import SHIcons from '@/components/ico/SHIcons'

import { getEmptyAchievementInfo } from '@/transformer/achievement'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'AchievementList',
  components: {
    AchievementDetail,
    AchievementEdit,
    Pagenation,
    ListHeader,
    ConfirmModal,
    AppButton,
    SHIcons,
    Typetalk
  },

  props: {
    showAll: Boolean,
    typetalkAuth: Boolean,
    achievementFields: Array,
    achievements: Array
  },

  computed: {
    ...mapState({
      achievementTypes: state => state.Achievement.achievementTypes,
      achievement: state => state.Achievement.achievement,
      listApiStatus: state => state.Achievement.ilistApiStatus,
      infoApiStatus: state => state.Achievement.infoApiStatus
    }),
    ...mapGetters('Achievement/', ['selectedAchievement']),
    achievementOptions() {
      const types = this.isAuth ? this.achievementTypes : this.achievementTypes.filter((e) => {
        return Number(e.available) === 1
      })
      return types.map((e) => {
        return { text: e.name, value: e.id }
      })
    },
    isAuth() {
      return baseJs.isAuth()
    },
    colSize() {
      if (baseJs.isAuth()) {
        return 6
      }
      return 4
    },
    iconClass() {
      if (baseJs.isAuth) {
        return 'icons admin'
      }
      return 'icons'
    },
    // 検索欄の内容で成果を絞り込む
    filteredAchievements() {
      if (!this.search) {
        return this.achievements
      }
      return listJs.getFilteredArray(this.achievements, this.achievementFields, this.search, this.pagenate)
    },

    searchPlaceHolder() {
      if (baseJs.isAuth()) {
        return '修了生名や成果タイトル・内容'
      }
      return 'タイトル・内容で検索'
    },

    tableFields() {
      return this.achievementFields.filter((e) => !e.hidden)
    }
  },

  data() {
    return {
      typetalkIcon: AppConfig.url + '/Typetalk_icon.png',
      isDetail: false,
      achievementTitle: '',
      achievementId: 0,
      detailIndex: 0,
      pagenate: listJs.getDefaultPagenate(),
      addButtonText: '成果を登録',
      achievementInfo: getEmptyAchievementInfo(),
      modalTitle: '成果を登録',
      search: listJs.getEmptySearch(this.achievementFields),
      timer: 0
    }
  },

  methods: {
    // ソート結果順に並べ替え
    changeSort: function (e) {
      this.pagenate.sortBy = e.sortBy
      this.pagenate.sortDesc = e.sortDesc
    },
    closeEditModal: function (edited) {
      if (edited) {
        this.isDetail = false
      }
      this.closeModal('achievement-modal')
    },

    // detailIndexをもとにデータを削除
    deleteAchievementFromDetail() {
      const achievement = this.filteredAchievements[this.detailIndex]
      this.deleteAchievement(achievement.achievement_id, achievement.title)
    },

    // 成果を削除する
    async deleteAchievement(achievementId, title) {
      this.$bvModal
        .msgBoxConfirm(`成果${title}を削除しますか？`, {
          title: '確認',
          okTitle: 'OK',
          okVariant: 'default',
          cancelTitle: 'キャンセル',
          hideHeaderClose: false
        })
        .then(async (bool) => {
          if (!bool) {
            return
          }

          try {
            await api.achievement._delete(achievementId)
            this.showSuccessToast(`成果「${title}」を削除しました。`)
            this.$emit('update', '')
            this.isDetail = false
          } catch (err) {
            const apiStatus = baseJs.getErrorStatus(err)
            const errorMessages = []
            for (const key in apiStatus.errors) {
              errorMessages.push(`・${apiStatus.errors[key]}`)
            }
            this.showErrorToast(errorMessages.join('\n'))
          }
        })
    },

    downloadList() {
      const records = this.filteredAchievements.map((achievement) => {
        return [
          this.escapeForCSV(achievement.user_name),
          this.escapeForCSV(achievement.title),
          this.escapeForCSV(this.getAchievementType(achievement.achievement_type_id, this.achievementTypes)),
          this.escapeForCSV(achievement.detail),
          this.escapeForCSV(achievement.link),
          this.escapeForCSV(dayjs(achievement.created_at).format('YYYY年MM月DD日')),
          this.escapeForCSV(dayjs(achievement.updated_at).format('YYYY年MM月DD日'))
        ]
      })
      records.unshift(['名前', 'タイトル', '種別', '詳細', '関連リンク', '作成日', '更新日'])
      downloadCsv(records, 'achievement_list')
      this.closeModal('downloadConfirm')
    },
    escapeForCSV(s) {
      if (!s) {
        return ''
      }
      // csvにした時に区切りがずれないようエスケープ TODO:もっとスマートにできると思う
      return s.replace(/"/g, '').replace(/,/g, '、').replace(/'/g, '').replace(/\n/g, '  ')
    },

    // 一覧から、IDを使って成果単体の情報を取得
    getAchievementIndexById(achievementId) {
      if (!achievementId || !this.filteredAchievements) {
        return null
      }
      let index = -1
      this.filteredAchievements.find((achievement, i) => {
        if (achievement.achievement_id === achievementId) {
          index = i
          return true
        }
      })
      return index
    },
    handleClickShare(message, achievementId) {
      api.typetalk.send(message, achievementId).then(() => {
        this.showSuccessToast('Typetalkに投稿しました。')
        this.$bvModal.hide('typetalk-modal')
        this.$emit('update', '')
      }).catch((err) => {
        if (err.response.status === 404) {
          this.$bvModal
            .msgBoxConfirm('投稿先トピックにアクセスできません。typetalkから一度ログアウトし、権限のあるアカウントで再度連携し直してください。', {
              title: 'エラー',
              okTitle: '連携画面へ',
              okVariant: 'default',
              cancelTitle: 'キャンセル',
              hideHeaderClose: false
            })
            .then(async (bool) => {
              if (bool) {
                this.$store.dispatch('typetalk/authorizeTypetalk')
              }
            })
        } else {
          this.showErrorToast('Typetalkへの投稿に失敗しました。')
        }
      })
    },
    async openEditForm(achievementId) {
      if (achievementId) {
        this.modalTitle = '成果の編集'
        if (!this.isDetail) {
          await this.$store.dispatch('Achievement/getAchievementInfo', achievementId)
        }
      } else {
        this.modalTitle = this.addButtonText
        await this.$store.commit('Achievement/setAchievement', getEmptyAchievementInfo())
        this.$store.commit('Achievement/setInfoApiStatus', baseJs.getApiStatusEmpty())
      }
      this.achievementInfo = Object.assign({}, this.selectedAchievement)
      this.$nextTick().then((e) => {
        this.$bvModal.show('achievement-modal')
      })
    },
    async handleClickTypetalk(title, achievementId) {
      try {
        await this.$store.dispatch('typetalk/checkIsTypetalkAuthenticated')
        this.openTypetalkForm(title, achievementId)
      } catch (error) {
        this.$store.dispatch('typetalk/authorizeTypetalk')
      }
    },
    openTypetalkForm(title, achievementId) {
      this.achievementTitle = title
      this.achievementId = Number(achievementId)
      this.$nextTick().then(() => {
        this.$bvModal.show('typetalk-modal')
      })
    },
    closeModal(id) {
      this.$bvModal.hide(id)
    },
    getArrangedIssuedDate(issuedAt) {
      if (!issuedAt) {
        return '未設定'
      }
      return dayjs(issuedAt).format('YYYY年MM月DD日')
    },
    getArrangedUpdatedAt(updatedAt, issuedAt) {
      if (issuedAt === updatedAt) {
        return ''
      }
      return dayjs(updatedAt).format('YYYY年MM月DD日')
    },

    // 成果の種別をテキストで取得する
    getAchievementType(typeId, typeList) {
      if (!typeList) {
        return '不明'
      }
      var data = typeList.find((achievementType) => {
        if (achievementType.id === typeId) {
          return true
        }
      })
      if (typeof data === 'object' && data.name) {
        return data.name
      }
      return '不明'
    },

    getDeleteConfirmMessage(title) {
      return '成果「' + title + '」を削除してよろしいですか？'
    },
    getDownloadConfirmMessage() {
      const text = ['以下の条件にあう成果の一覧をダウンロードします。']
      if (this.search.text) {
        text.push('・' + this.searchPlaceHolder + 'に「' + this.search.text + '」を含む')
      }
      let tmpText = ''
      let tmpObj = {}
      Object.keys(this.search.select).map((key) => {
        if (this.search.select[key] !== 'all') {
          tmpObj = this.achievementFields.find((field) => {
            return field.key === key
          })
          tmpText = '・' + tmpObj.label + 'が' + tmpObj.selectSearch.find(e => e.value === this.search.select[key]).text
          text.push(tmpText)
        }
      })
      Object.keys(this.search.date).map((key) => {
        if (this.search.date[key].before !== null || this.search.date[key].before !== null) {
          tmpObj = this.achievementFields.find((field) => {
            return field.key === key
          })
          tmpText = '・' + tmpObj.label + 'が'
          if (this.search.date[key].after) {
            tmpText += dayjs(this.search.date[key].after).format('YYYY年MM月DD日') + '以降'
          }
          if (this.search.date[key].before) {
            tmpText += dayjs(this.search.date[key].before).format('YYYY年MM月DD日') + '以前'
          }
          text.push(tmpText)
        }
      })
      if (text.length <= 1) {
        text.push('・全ての成果')
      }
      return text
    },
    toString(value) {
      if (value === null || typeof value === 'undefined') {
        return ''
      } else if (value instanceof Object) {
        return Object.keys(value)
          .sort()
          .map((key) => toString(value[key]))
          .join(' ')
      } else {
        return String(value)
      }
    },

    async showDetail(achievementId) {
      this.$store.dispatch('Achievement/getAchievementInfo', achievementId)
      this.detailIndex = this.getAchievementIndexById(achievementId)
      this.isDetail = true
    },

    showOther(index) {
      const achievementId = this.filteredAchievements[index].achievement_id
      this.showDetail(achievementId)
    },
    showList() {
      this.$emit('update')
      this.isDetail = false
    },
    changePage: function (pagenate) {
      this.pagenate = pagenate
    },
    sortCompare(a, b, key = this.pagenate.sortBy, sortDesc = this.pagenate.sortDesc) {
      let compare = true
      if (
        (typeof a[key] === 'number' && typeof b[key] === 'number') ||
        (a[key] instanceof Date && b[key] instanceof Date)
      ) {
        // If both compared fields are native numbers or both are native dates
        compare = a[key] >= b[key]
      } else {
        // Otherwise stringify the field data and use String.prototype.localeCompare
        compare = this.toString(b[key]).localeCompare(toString(a[key]))
      }
      if (sortDesc === compare) {
        return -1
      } else {
        return 1
      }
    },
    showErrorToast(message) {
      this.$root.$bvToast.toast(message, {
        variant: 'danger',
        toaster: 'b-toaster-top-center',
        toastClass: 'pre-wrap',
        noAutoHide: true
      })
    },
    showSuccessToast(message) {
      this.$root.$bvToast.toast(message, {
        variant: 'success',
        toaster: 'b-toaster-top-center',
        autoHideDelay: 3000
      })
    },
    showTypetalk(id) {
      const url = 'https://typetalk.com/topics/' + AppConfig.typetalkTopicId + '/posts/' + id
      window.open(url, '_blank')
    },
    stateText(isDraft) {
      return this.$constList.stateList[isDraft]
    },
    updateSearch(e) {
      this.search = e
      this.timer = 3
    },
    update(successMessage) {
      this.$emit('update', successMessage)
    }
  },
  created() {
    setInterval(() => {
      // 3秒間検索条件が操作されなかったら検索条件を履歴に残す
      const timer = this.timer
      if (timer === 1) {
        let functionId = 4
        if (baseJs.isAuth()) {
          functionId = 3
        }
        api.log.logSearchHistory(this.search, functionId)
      }
      if (timer > 0) {
        this.timer--
      }
    }, 1000)
    if (this.isAuth) {
      this.$store.dispatch('Achievement/getProgramTimeAndCourse')
    }
  }
}
</script>

<style scoped>
.draft {
  font-size: 12px;
}

.edit-achievement:hover,
.locate-typetalk:hover,
.delete-achievement:hover,
.share-achievement:hover {
  cursor: pointer;
  opacity: 0.9;
}

.share-achievement{
  width: 18px;
  height: 18px;
  vertical-align: middle;
}
.locate-typetalk{
  vertical-align: middle;
}

.icons{
  align-items: center;
  vertical-align: middle;
  font-size: 18px;
  color: #68c3d4;
  width: calc((2rem + 18px) * 3);
}

.icons.admin{
  width: calc((2rem + 18px) * 2);
}
.announcement-table >>> .management{
  text-align: center;
  vertical-align: middle;
}
</style>
